import { createTabApi, deleteTabApi, renameTabApi, setPredefinedTabsStatusApi, setTabActiveApi, sortTabsApi, type TabItemModel } from '@/api/TabsEndpoints/tabs.api';
import { selectProfileId, selectTranslation } from '@store/slices/account/accountSlice.js';
import { addCustomTab, removeCustomTab, selectTabs, updateCustomTab, updateTabs } from '@store/slices/design/designSlice.js';
import { predefinedTabAddRequest, tabCreateRequest, tabDeleteRequest, tabRenameRequest, tabsSortRequest, tabToggleRequest } from '@store/slices/design/tabsActions';
import { toastrShowError, toastrShowSuccess } from '@store/toolkit/toastr/toastrActions.js';
import { getApiErrorMessage } from '@utils/apiErrorMessage.js';
import type { AxiosResponse } from 'axios';
import type { SagaIterator } from 'redux-saga';
import type { StrictEffect } from 'redux-saga/effects';
import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';

// All tab CRUD flows live here — components dispatch the request actions from tabsActions.ts.
// Mutations are optimistic (store first, API second) with rollback on failure, except create
// (nothing to show until the backend has assigned id/customKey).

interface TabsState {
  items?: TabItemModel[];
  [key: string]: unknown;
}

const itemId = (t: TabItemModel) => t.id ?? t.Id;
const itemType = (t: TabItemModel) => t.type ?? t.Type;

// User-facing strings. `components.customTab` has no entry in the translation files yet
// (the whole custom-tab feature is English-only today) — going through the same lookup the
// rest of the feature uses means these localize for free once the key block lands.
// Typed as a Generator rather than SagaIterator: the latter is only an Iterator, and `yield*`
// needs an Iterable whose yield type still matches the calling saga's.
function* tabMessage(key: string, fallback: string): Generator<StrictEffect, string, any> {
  const translation = yield select(selectTranslation);
  return translation?.components?.customTab?.[key] || fallback;
}

function* createTabSaga(action: ReturnType<typeof tabCreateRequest>): SagaIterator {
  const { blockType, title, icon, onSuccess, onDone } = action.payload;
  try {
    const profileId: number = yield select(selectProfileId);
    const tabs: TabsState = yield select(selectTabs);
    const items = tabs.items ?? [];
    // Append after the max ordinal — `items.length + 1` collides with existing n values
    // because the list also holds not-added predefined tabs with sparse numbering.
    const nextN = items.reduce((max, t) => Math.max(max, t.n ?? t.N ?? 0), 0) + 1;
    const response: AxiosResponse<TabItemModel> = yield call(createTabApi, profileId, {
      type: 'custom',
      blockType,
      title,
      icon,
      isActive: true,
      n: nextN,
    });
    yield put(addCustomTab(response.data));
    onSuccess?.(response.data);
  } catch (error) {
    yield put(toastrShowError(getApiErrorMessage(error) || (yield* tabMessage('createError', 'Failed to create custom tab'))));
  } finally {
    onDone?.();
  }
}

function* deleteTabSaga(action: ReturnType<typeof tabDeleteRequest>): SagaIterator {
  const { item, onSuccess, onDone } = action.payload;
  try {
    const tabId = itemId(item);
    if (tabId == null) return;
    const isCustom = itemType(item) === 'custom';

    const profileId: number = yield select(selectProfileId);
    const tabs: TabsState = yield select(selectTabs);
    const prevItems = tabs.items ?? [];

    if (!isCustom) {
      // Optimistic: a predefined "delete" only unsets IsAdded server-side.
      yield put(updateTabs({ ...tabs, items: prevItems.filter((t) => itemId(t) !== tabId) }));
    }

    try {
      yield call(deleteTabApi, profileId, tabId);
      if (isCustom) yield put(removeCustomTab(tabId));
      onSuccess?.();
    } catch (error) {
      if (!isCustom) yield put(updateTabs({ ...tabs, items: prevItems }));
      yield put(toastrShowError(getApiErrorMessage(error) || (yield* tabMessage('deleteError', 'Failed to delete tab'))));
    }
  } finally {
    onDone?.();
  }
}

function* renameTabSaga(action: ReturnType<typeof tabRenameRequest>): SagaIterator {
  const { tabId, title, onDone } = action.payload;
  try {
    const profileId: number = yield select(selectProfileId);
    const tabs: TabsState = yield select(selectTabs);
    const prev = (tabs.items ?? []).find((t) => itemId(t) === tabId);
    const prevTitle = prev?.title ?? prev?.Title ?? '';

    // updateCustomTab merges into items by id, so it covers predefined (added) tabs too.
    yield put(updateCustomTab({ id: tabId, title }));

    try {
      yield call(renameTabApi, profileId, tabId, title);
      yield put(toastrShowSuccess(yield* tabMessage('renamed', 'Tab renamed')));
    } catch (error) {
      yield put(updateCustomTab({ id: tabId, title: prevTitle }));
      yield put(toastrShowError(getApiErrorMessage(error) || (yield* tabMessage('renameError', 'Failed to rename tab'))));
    }
  } finally {
    onDone?.();
  }
}

function* toggleTabSaga(action: ReturnType<typeof tabToggleRequest>): SagaIterator {
  const { item, isActive } = action.payload;
  const targetId = itemId(item);
  const isCustom = itemType(item) === 'custom';

  // A custom tab is addressed by id only. Without one there is nothing to persist, so bail
  // before the optimistic update — otherwise the store would silently drift from the backend.
  if (isCustom && targetId == null) return;

  const profileId: number = yield select(selectProfileId);
  const tabs: TabsState = yield select(selectTabs);
  const prevItems = tabs.items ?? [];

  // Custom tabs match by id (they all share type='custom'), predefined by type.
  const nextItems = prevItems.map((t) => {
    const matched = isCustom ? itemId(t) === targetId : itemType(t) === itemType(item);
    return matched ? { ...t, isActive } : t;
  });
  yield put(updateTabs({ ...tabs, items: nextItems }));

  try {
    if (isCustom) {
      yield call(setTabActiveApi, profileId, targetId as number, isActive);
    } else {
      // status endpoint must never see custom entries — see setPredefinedTabsStatusApi.
      yield call(
        setPredefinedTabsStatusApi,
        profileId,
        nextItems.filter((t) => itemType(t) !== 'custom'),
      );
    }
  } catch (error) {
    yield put(updateTabs({ ...tabs, items: prevItems }));
    yield put(toastrShowError(getApiErrorMessage(error) || (yield* tabMessage('visibilityError', 'Failed to update tab visibility'))));
  }
}

function* sortTabsSaga(action: ReturnType<typeof tabsSortRequest>): SagaIterator {
  const { items } = action.payload;
  const profileId: number = yield select(selectProfileId);
  const tabs: TabsState = yield select(selectTabs);
  const prevItems = tabs.items ?? [];

  yield put(updateTabs({ ...tabs, items }));

  try {
    // Only backend-known rows can be re-ordered server-side; predefined rows are matched
    // by type, custom rows by id.
    yield call(
      sortTabsApi,
      profileId,
      items.map((t) => ({ id: itemId(t), type: itemType(t), n: t.n ?? t.N })),
    );
  } catch (error) {
    yield put(updateTabs({ ...tabs, items: prevItems }));
    yield put(toastrShowError(getApiErrorMessage(error) || (yield* tabMessage('sortError', 'Failed to save tab order'))));
  }
}

function* addPredefinedTabSaga(action: ReturnType<typeof predefinedTabAddRequest>): SagaIterator {
  const { item, onDone } = action.payload;
  try {
    const profileId: number = yield select(selectProfileId);
    const response: AxiosResponse<TabItemModel> = yield call(createTabApi, profileId, { ...item, type: itemType(item) ?? '', isAdded: true });

    const tabs: TabsState = yield select(selectTabs);
    const type = itemType(item);
    // The type may already sit in the list as isAdded:false (exists, not added as a nav
    // tab) — replace by type instead of appending to avoid a duplicate entry.
    yield put(updateTabs({ ...tabs, items: [...(tabs.items ?? []).filter((t) => itemType(t) !== type), response.data] }));
  } catch (error) {
    yield put(toastrShowError(getApiErrorMessage(error) || (yield* tabMessage('addError', 'Failed to add tab'))));
  } finally {
    onDone?.();
  }
}

export function* tabsWatcher(): SagaIterator {
  yield takeEvery(tabCreateRequest.type, createTabSaga);
  yield takeEvery(tabDeleteRequest.type, deleteTabSaga);
  yield takeEvery(tabRenameRequest.type, renameTabSaga);
  yield takeEvery(tabToggleRequest.type, toggleTabSaga);
  yield takeLatest(tabsSortRequest.type, sortTabsSaga);
  yield takeEvery(predefinedTabAddRequest.type, addPredefinedTabSaga);
}
